import React, { FC } from 'react';
import { HotelSearchFailure, HotelSearchHit, HotelSearchTab, useButton, useI18n } from '~Hooks';
import { Box, Input, SearchInputWrapper, GhostButton } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { ImpulseSpinner } from '~Components/ImpulseSpinner';
import SegmentedControl from '~Components/SegmentedControl';
import ErrorNote from '~Components/ErrorNote';
import SearchResult from '../SearchResult';

interface Props {
  tab: HotelSearchTab;
  search: string;
  /** True once the debounced query is long enough to have been sent. */
  searching: boolean;
  loading: boolean;
  failure: HotelSearchFailure;
  results: HotelSearchHit[];
  /** Shown above the results, e.g. after a charge was undone. */
  notice?: string;
  onTabChange: (tab: HotelSearchTab) => void;
  onSearchChange: (value: string) => void;
  onSelect: (hit: HotelSearchHit) => void;
  onCancel: () => void;
}

/** Finds the guest (room number or last name) or company account to charge. */
const SearchStep: FC<Props> = ({
  tab,
  search,
  searching,
  loading,
  failure,
  results,
  notice,
  onTabChange,
  onSearchChange,
  onSelect,
  onCancel,
}) => {
  const { i18n } = useI18n();
  const cancelButton = useButton({ onClick: onCancel });

  return (
    <Box fluid display="flex" flexDirection="column" gap="medium">
      <SegmentedControl<HotelSearchTab>
        value={tab}
        onChange={onTabChange}
        options={[
          { value: 'GUEST', label: i18n('HotelRoom.Guest') },
          { value: 'ACCOUNT', label: i18n('HotelRoom.CompanyAccount') },
        ]}
      />

      <SearchInputWrapper fluid value={search} onClear={() => onSearchChange('')}>
        <Input
          value={search}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            onSearchChange(event.target.value)
          }
          placeholder={
            tab === 'GUEST' ? i18n('HotelRoom.SearchGuest') : i18n('HotelRoom.SearchAccount')
          }
          dark
          isSearch
          fluid
          autoFocus
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
        />
      </SearchInputWrapper>

      {notice && (
        <Paragraph level={3} color="var(--colorBlueGray200)">
          {notice}
        </Paragraph>
      )}

      {searching && loading && (
        <Box display="flex" justifyContent="center" padding="medium">
          <ImpulseSpinner size={32} />
        </Box>
      )}

      {searching && !loading && failure && (
        <ErrorNote>
          {failure === 'MAPPING_MISSING'
            ? i18n('HotelRoom.NotSetUp')
            : i18n('HotelRoom.Unreachable')}
        </ErrorNote>
      )}

      {searching && !loading && !failure && results.length === 0 && (
        <Paragraph level={4} color="var(--colorBlueGray200)" style={{ textAlign: 'center' }}>
          {i18n('HotelRoom.NoResults')}
        </Paragraph>
      )}

      {!loading && results.length > 0 && (
        <Box
          fluid
          display="flex"
          flexDirection="column"
          gap="xs"
          style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {results.map((hit) => (
            <SearchResult key={hit.key} hit={hit} onSelect={onSelect} />
          ))}
        </Box>
      )}

      <Box fluid style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: 8 }}>
        <GhostButton {...cancelButton} size="large">
          {i18n('HotelRoom.Cancel')}
        </GhostButton>
      </Box>
    </Box>
  );
};

export default SearchStep;
